import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Select } from "@/components/ui/select"
import { ConsultationBand, PublicHero, PublicShell } from "@/components/PublicShell"
import { PublicMeta } from "@/components/public/PublicMeta"
import { WineCard, type WineCardData } from "@/components/public/WineCard"
import type { PageProps } from "@/types/inertia"
import "@/styles/public-wines.css"

const TITLE = "Our Wines: Luxury South African Wine"
const DESCRIPTION =
  "The luxury South African wine we carry for bespoke labels: Cap Classique, red, white and rosé from 50+ Cape winemakers, delivered throughout the UK."

type Option = { value: string; label: string }

type Filters = {
  types: string[]
  taste: string | null
  band: string | null
  sort: string
}

type WinesProps = {
  wines: WineCardData[]
  filters: Filters
  filter_options: {
    types: (Option & { count: number })[]
    tastes: Option[]
    bands: Option[]
    sorts: Option[]
  }
  total: number
  awaiting_pricing: number
  mcc_explainer: string
}

const DEFAULT_SORT = "name"

// Only non-default values go into the URL, so the plain /wines link stays clean.
function toQuery(filters: Filters) {
  const query: Record<string, string | string[]> = {}
  if (filters.types.length) query.type = filters.types
  if (filters.taste) query.taste = filters.taste
  if (filters.band) query.band = filters.band
  if (filters.sort !== DEFAULT_SORT) query.sort = filters.sort
  return query
}

function isFiltered(filters: Filters) {
  return filters.types.length > 0 || Boolean(filters.taste) || Boolean(filters.band) || filters.sort !== DEFAULT_SORT
}

export default function Wines() {
  const { props, url } = usePage<PageProps<WinesProps>>()
  const { wines, filters, filter_options: options, total, awaiting_pricing: awaitingPricing, mcc_explainer: mccExplainer } = props

  // Before hydration the filter bar is a plain GET form with an Apply button;
  // once React is running, every change applies immediately.
  const [hydrated, setHydrated] = React.useState(false)
  React.useEffect(() => setHydrated(true), [])

  const apply = (next: Partial<Filters>) => {
    router.get("/wines", toQuery({ ...filters, ...next }), {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
  }

  const toggleType = (value: string, checked: boolean) => {
    const types = checked ? [...filters.types, value] : filters.types.filter((t) => t !== value)
    // Keep the server's option order so URLs are stable.
    apply({ types: options.types.map((o) => o.value).filter((v) => types.includes(v)) })
  }

  const countLine = `${total} ${total === 1 ? "wine" : "wines"}`

  return (
    <PublicShell>
      <PublicMeta title={TITLE} description={DESCRIPTION} />
      <PublicHero
        eyebrow="The Range"
        title="Our Wines"
        lede="Cap Classique, red, white and rosé from South African cellars — chosen because they are worth putting your name on."
        actions={
          <>
            <Button asChild variant="gold" size="lg">
              <Link href="/contact">Request a Consultation</Link>
            </Button>
            <Button asChild variant="ghost-hairline" size="lg">
              <Link href="/finder">Find your wine</Link>
            </Button>
          </>
        }
      />

      <section className="band-black wine-range" aria-labelledby="range-title">
        <div className="shell">
          <h2 id="range-title" className="sr-only">
            The range
          </h2>

          <form
            method="get"
            action="/wines"
            className="wine-filters"
            aria-label="Filter the range"
            onSubmit={(event) => {
              event.preventDefault()
              apply({})
            }}
          >
            <fieldset>
              <legend className="field-label">Wine type</legend>
              <div className="wine-filter-chips">
                {options.types.map((option) => (
                  <label key={option.value} className="wine-chip">
                    <input
                      type="checkbox"
                      name="type[]"
                      value={option.value}
                      checked={filters.types.includes(option.value)}
                      onChange={(event) => toggleType(option.value, event.target.checked)}
                    />
                    <span className="wine-chip-face">
                      {option.label}
                      <span className="wine-chip-count" aria-label={`${option.count} wines`}>
                        {option.count}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              {options.types.some((o) => o.value === "mcc" || o.value === "mcc_rose") && (
                <div className="field-hint">MCC (Méthode Cap Classique): {mccExplainer}</div>
              )}
            </fieldset>

            <fieldset>
              <legend className="field-label">Taste</legend>
              <div className="wine-filter-chips">
                {[{ value: "", label: "Any" }, ...options.tastes].map((option) => (
                  <label key={option.value || "any"} className="wine-chip">
                    <input
                      type="radio"
                      name="taste"
                      value={option.value}
                      checked={(filters.taste ?? "") === option.value}
                      onChange={() => apply({ taste: option.value || null })}
                    />
                    <span className="wine-chip-face">{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div>
              <label htmlFor="wine-band" className="field-label">
                Price per bottle
              </label>
              <Select
                id="wine-band"
                name="band"
                className="form-control-cellar"
                value={filters.band ?? ""}
                onChange={(event) => apply({ band: event.target.value || null })}
              >
                <option value="">Any price</option>
                {options.bands.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label htmlFor="wine-sort" className="field-label">
                Sort by
              </label>
              <Select
                id="wine-sort"
                name="sort"
                className="form-control-cellar"
                value={filters.sort}
                onChange={(event) => apply({ sort: event.target.value })}
              >
                {options.sorts.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="wine-filters-foot">
              <span className="wine-filters-count" aria-live="polite">
                {countLine}
              </span>
              <div className="wine-filters-actions">
                {!hydrated && (
                  <Button type="submit" variant="ghost-hairline" size="sm">
                    Apply
                  </Button>
                )}
                {isFiltered(filters) && (
                  <Link href="/wines" className="wine-filters-clear" preserveScroll>
                    Clear filters
                  </Link>
                )}
              </div>
            </div>
          </form>

          {filters.band && awaitingPricing > 0 && (
            <p className="wine-filters-note">
              {awaitingPricing} {awaitingPricing === 1 ? "wine awaiting pricing is" : "wines awaiting pricing are"} not
              shown when filtering by price.
            </p>
          )}

          {wines.length > 0 ? (
            <div className="wine-grid">
              {wines.map((wine) => (
                <WineCard key={wine.id} wine={wine} mccExplainer={mccExplainer} returnTo={url} />
              ))}
            </div>
          ) : (
            <div className="wine-empty">
              <p>Nothing in the range matches that combination.</p>
              <p>
                <Link href="/wines">See the whole range</Link> or <Link href="/finder">let the finder suggest a wine</Link>.
              </p>
            </div>
          )}
        </div>
      </section>

      <ConsultationBand />
    </PublicShell>
  )
}
